/**
 * Admin: DMS Doc-Type-Katalog (D1).
 *
 * 2-Spalten-Editor:
 *   Links  — Liste aller Doc-Types (Filter aktiv/inaktiv, Neu anlegen).
 *   Rechts — Detail des gewaehlten Typs (Metadaten + Feld-Liste + Feld-Editor).
 *
 * Seit 2026-09-30 (Regel „Dokument-Navigation" E10, Abhakliste 1.46): Die Metadaten
 * des Typs sind ein Einstellungs-Abschnitt (Anzeige zuerst, „Bearbeiten" öffnet,
 * „Speichern"/„Abbrechen" zurück in die Anzeige). Die Feld-Liste bleibt Listen-Pflege
 * in der Zeile (Ausnahme E4) mit Speichern/Abbrechen je Zeile; Löschen fragt über
 * das Portal-Dialogfenster nach, nie über das Fenster des Browsers.
 *
 * Nur fuer Admins bzw. Nutzer mit `dms.configure`-Permission sichtbar.
 * Zugehoeriger Backend-Container: `engine/apps/dms/src/routes.ts`.
 */
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Plus, Pencil, Trash2, Loader2, FileText, Save, X } from 'lucide-react';
import { SettingsSection, useDialogs } from '@onvice/portal-ui';

const BASE = '/api/modules/dms';

const FIELD_KINDS = [
  { value: 'text',         label: 'Text' },
  { value: 'multitext',    label: 'Mehrzeiliger Text' },
  { value: 'number',       label: 'Zahl' },
  { value: 'currency',     label: 'Währung' },
  { value: 'date',         label: 'Datum' },
  { value: 'datetime',     label: 'Datum + Zeit' },
  { value: 'boolean',      label: 'Ja/Nein' },
  { value: 'select',       label: 'Auswahl' },
  { value: 'customer_ref', label: 'Kontakt-Referenz' },
  { value: 'person_ref',   label: 'Personen-Referenz' },
] as const;
type FieldKind = typeof FIELD_KINDS[number]['value'];

interface DocType {
  id: string;
  slug: string;
  label: string;
  icon: string | null;
  color: string | null;
  retention_years: number | null;
  retention_action: 'alert' | 'archive' | null;
  classifier_model: string | null;
  active: boolean;
  sort_order: number;
  created_at: string;
}

interface DocField {
  id: string;
  doc_type_id: string;
  slug: string;
  label: string;
  kind: FieldKind;
  options: unknown;
  required: boolean;
  ai_extract: boolean;
  sort_order: number;
}

interface DocTypeDetail extends DocType { fields: DocField[] }

/** Arbeitskopie der Typ-Metadaten (Formularwerte als Text). */
interface TypeMeta {
  label: string;
  color: string;
  retentionYears: string;
  retentionAction: 'alert' | 'archive';
  classifierModel: string;
  active: boolean;
}

async function api<T = unknown>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(BASE + path, { credentials: 'include', ...init });
  const j = await r.json().catch(() => ({} as { success?: boolean; error?: string }));
  if (!r.ok || j?.success === false) {
    throw new Error(j?.error ?? `HTTP ${r.status}`);
  }
  return j.data as T;
}

const INPUT = 'w-full h-9 px-3 rounded-md border border-slate-700 bg-slate-800 text-sm text-slate-100';

export default function AdminDmsTypesPage() {
  const [types, setTypes] = useState<DocType[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<DocTypeDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function loadTypes() {
    setLoading(true); setErr(null);
    try {
      const rows = await api<DocType[]>('/doc-types');
      setTypes(rows);
      if (!selectedId && rows.length > 0) setSelectedId(rows[0].id);
    } catch (e) { setErr((e as Error).message); }
    finally { setLoading(false); }
  }

  async function loadDetail(id: string) {
    setDetailLoading(true);
    try { setDetail(await api<DocTypeDetail>(`/doc-types/${id}`)); }
    catch (e) { setErr((e as Error).message); setDetail(null); }
    finally { setDetailLoading(false); }
  }

  useEffect(() => { void loadTypes(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => { if (selectedId) void loadDetail(selectedId); }, [selectedId]);

  const filteredTypes = useMemo(
    () => types.filter((t) => showInactive || t.active),
    [types, showInactive],
  );

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-test-anchor="dms-typen">
      <div className="flex items-center gap-3 mb-4">
        <FileText size={22} className="text-brand-500" />
        <h1 className="text-xl font-semibold text-slate-100">DMS Dokumenttypen</h1>
      </div>
      {err && <div className="mb-3 p-3 rounded-md bg-rose-950/60 border border-rose-800 text-rose-200 text-sm" data-test-anchor="dms-typen-fehler">{err}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4">
        {/* Linke Spalte: Type-Liste */}
        <aside className="bg-slate-900/60 border border-slate-800 rounded-lg overflow-hidden flex flex-col">
          <div className="px-3 py-2 border-b border-slate-800 flex items-center justify-between">
            <span className="text-xs uppercase tracking-wider text-slate-500">Typen</span>
            <label className="text-xs text-slate-400 flex items-center gap-1">
              <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
              inaktive
            </label>
          </div>
          {loading ? (
            <div className="p-4 text-slate-400 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> lädt …</div>
          ) : (
            <ul className="flex-1 overflow-y-auto">
              {filteredTypes.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(t.id)}
                    className={`w-full text-left px-3 py-2 border-b border-slate-800/60 hover:bg-slate-800/60 flex items-center justify-between gap-2 ${
                      selectedId === t.id ? 'bg-slate-800/80' : ''
                    } ${t.active ? '' : 'opacity-50'}`}
                  >
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ background: t.color ?? '#64748B' }} />
                      <span className="text-sm text-slate-100">{t.label}</span>
                    </span>
                    <span className="text-xs text-slate-500">{t.slug}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="p-2 border-t border-slate-800">
            <button
              type="button"
              onClick={() => { setSelectedId(null); setDetail(null); }}
              className="w-full inline-flex items-center justify-center gap-2 py-1.5 text-xs rounded-md bg-brand-600 hover:bg-brand-500 text-primary-foreground min-h-[44px]"
            >
              <Plus size={14} /> Neu anlegen
            </button>
          </div>
        </aside>

        {/* Rechte Spalte: Detail */}
        <section className="bg-slate-900/60 border border-slate-800 rounded-lg p-4">
          {selectedId === null ? (
            <TypeCreateForm onCreated={async (t) => { await loadTypes(); setSelectedId(t.id); }} />
          ) : detailLoading || !detail ? (
            <div className="text-slate-400 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> lädt Detail …</div>
          ) : (
            <TypeDetailEditor
              detail={detail}
              onChanged={async () => { await loadTypes(); await loadDetail(selectedId); }}
              onDeleted={async () => { await loadTypes(); setSelectedId(null); setDetail(null); }}
            />
          )}
        </section>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
// Neuer Typ anlegen (Neuanlage: Formular bleibt sofort bearbeitbar, Annahme 1)
// ---------------------------------------------------------------
function TypeCreateForm({ onCreated }: { onCreated: (t: DocType) => void | Promise<void> }) {
  const [slug, setSlug] = useState('');
  const [label, setLabel] = useState('');
  const [color, setColor] = useState('#3B82F6');
  const [retentionYears, setRetentionYears] = useState<string>('');
  const [retentionAction, setRetentionAction] = useState<'alert' | 'archive'>('alert');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const created = await api<DocType>('/doc-types', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slug: slug.trim(), label: label.trim(), color,
          retention_years: retentionYears ? Number(retentionYears) : null,
          retention_action: retentionYears ? retentionAction : null,
        }),
      });
      await onCreated(created);
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }

  return (
    <form onSubmit={submit} className="space-y-3 max-w-md" data-test-anchor="dms-typ-neu">
      <h2 className="text-lg font-semibold text-slate-100 mb-2">Neuer Dokumenttyp</h2>
      {err && <div className="p-2 rounded bg-rose-950/60 border border-rose-800 text-rose-200 text-sm">{err}</div>}
      <label className="block text-sm">
        <span className="block text-xs text-slate-500 mb-1">Slug (a-z0-9_-, unveränderlich)</span>
        <input required value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))} className={INPUT} />
      </label>
      <label className="block text-sm">
        <span className="block text-xs text-slate-500 mb-1">Anzeigename</span>
        <input required value={label} onChange={(e) => setLabel(e.target.value)} className={INPUT} />
      </label>
      <div className="grid grid-cols-2 gap-2">
        <label className="block text-sm">
          <span className="block text-xs text-slate-500 mb-1">Farbe</span>
          <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="w-full h-9 rounded" />
        </label>
        <label className="block text-sm">
          <span className="block text-xs text-slate-500 mb-1">Aufbewahrung (Jahre)</span>
          <input type="number" min={0} value={retentionYears} onChange={(e) => setRetentionYears(e.target.value)} className={INPUT} />
        </label>
      </div>
      {retentionYears && (
        <label className="block text-sm">
          <span className="block text-xs text-slate-500 mb-1">Bei Ablauf</span>
          <select value={retentionAction} onChange={(e) => setRetentionAction(e.target.value as 'alert' | 'archive')} className={INPUT}>
            <option value="alert">Nur Alarm (Admin entscheidet)</option>
            <option value="archive">Automatisch archivieren</option>
          </select>
        </label>
      )}
      <button type="submit" disabled={busy || !slug || !label}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand-600 hover:bg-brand-500 text-primary-foreground disabled:opacity-50 min-h-[44px]">
        {busy && <Loader2 size={14} className="animate-spin" />} Anlegen
      </button>
    </form>
  );
}

// ---------------------------------------------------------------
// Detail-Editor (Metadaten als Einstellungs-Abschnitt + Felder)
// ---------------------------------------------------------------
function metaOf(detail: DocTypeDetail): TypeMeta {
  return {
    label: detail.label,
    color: detail.color ?? '#3B82F6',
    retentionYears: detail.retention_years?.toString() ?? '',
    retentionAction: detail.retention_action ?? 'alert',
    classifierModel: detail.classifier_model ?? '',
    active: detail.active,
  };
}

function TypeDetailEditor({ detail, onChanged, onDeleted }: {
  detail: DocTypeDetail;
  onChanged: () => void | Promise<void>;
  onDeleted: () => void | Promise<void>;
}) {
  const dlg = useDialogs();
  const [busy, setBusy] = useState(false);
  const baseline = useMemo(() => metaOf(detail), [detail]);

  const saveMeta = useCallback(async (v: TypeMeta): Promise<TypeMeta> => {
    await api(`/doc-types/${detail.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        label: v.label.trim(), color: v.color,
        retention_years: v.retentionYears ? Number(v.retentionYears) : null,
        retention_action: v.retentionYears ? v.retentionAction : null,
        classifier_model: v.classifierModel.trim() || null,
        active: v.active,
      }),
    });
    await onChanged();
    return { ...v, label: v.label.trim(), classifierModel: v.classifierModel.trim() };
  }, [detail.id, onChanged]);

  async function delType() {
    const ok = await dlg.confirm({
      title: `Dokumenttyp „${detail.label}“ löschen?`,
      message: 'Wenn schon Dokumente dieses Typs existieren, wird der Typ nur deaktiviert.',
      confirmLabel: 'Löschen',
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try { await api(`/doc-types/${detail.id}`, { method: 'DELETE' }); await onDeleted(); }
    catch (e) { await dlg.error('Dokumenttyp wurde nicht gelöscht', e); }
    finally { setBusy(false); }
  }

  return (
    <div className="space-y-6" data-test-anchor="dms-typ-detail">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-100">{detail.label}</h2>
          <div className="text-xs text-slate-500 font-mono">{detail.slug}</div>
        </div>
        <button type="button" onClick={() => void delType()} disabled={busy}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-md border border-rose-700 text-rose-300 hover:bg-rose-950 disabled:opacity-50 min-h-[44px]">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Löschen
        </button>
      </div>

      <SettingsSection<TypeMeta>
        id={`dms-typ:${detail.id}`}
        title="Eigenschaften"
        baseline={baseline}
        testAnchor="dms-typ-meta"
        validate={(v) => {
          const issues: string[] = [];
          if (!v.label.trim()) issues.push('Anzeigename fehlt.');
          if (v.retentionYears && (!Number.isFinite(Number(v.retentionYears)) || Number(v.retentionYears) < 0)) issues.push('Aufbewahrung muss eine Zahl ab 0 sein.');
          return issues;
        }}
        onSave={saveMeta}
        renderRead={(v) => (
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div><dt className="text-xs text-slate-500">Anzeigename</dt><dd className="text-slate-100">{v.label}</dd></div>
            <div><dt className="text-xs text-slate-500">Farbe</dt><dd className="flex items-center gap-2 text-slate-100"><span className="w-3 h-3 rounded-full inline-block" style={{ background: v.color }} /> {v.color}</dd></div>
            <div><dt className="text-xs text-slate-500">Aufbewahrung</dt><dd className="text-slate-100">{v.retentionYears ? `${v.retentionYears} Jahre — bei Ablauf: ${v.retentionAction === 'archive' ? 'Auto-archivieren' : 'Alarm'}` : 'keine Frist'}</dd></div>
            <div><dt className="text-xs text-slate-500">Klassifikator-Modell (Ollama)</dt><dd className="text-slate-100">{v.classifierModel || <span className="text-slate-500">Standard</span>}</dd></div>
            <div><dt className="text-xs text-slate-500">Status</dt><dd className="text-slate-100">{v.active ? 'aktiv' : 'inaktiv'}</dd></div>
          </dl>
        )}
        renderEdit={(v, setField) => (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="block text-xs text-slate-500 mb-1">Anzeigename</span>
              <input value={v.label} onChange={(e) => setField('label', e.target.value)} className={INPUT} />
            </label>
            <label className="block text-sm">
              <span className="block text-xs text-slate-500 mb-1">Farbe</span>
              <input type="color" value={v.color} onChange={(e) => setField('color', e.target.value)} className="w-24 h-9 rounded" />
            </label>
            <label className="block text-sm">
              <span className="block text-xs text-slate-500 mb-1">Aufbewahrung (Jahre) — leer = keine Frist</span>
              <input type="number" min={0} value={v.retentionYears} onChange={(e) => setField('retentionYears', e.target.value)} className={INPUT} />
            </label>
            <label className="block text-sm">
              <span className="block text-xs text-slate-500 mb-1">Bei Ablauf</span>
              <select value={v.retentionAction} onChange={(e) => setField('retentionAction', e.target.value as 'alert' | 'archive')}
                      disabled={!v.retentionYears}
                      className={`${INPUT} disabled:opacity-50`}>
                <option value="alert">Alarm</option>
                <option value="archive">Auto-archivieren</option>
              </select>
            </label>
            <label className="block text-sm">
              <span className="block text-xs text-slate-500 mb-1">Klassifikator-Modell (Ollama)</span>
              <input value={v.classifierModel} onChange={(e) => setField('classifierModel', e.target.value)}
                     placeholder="z.B. german-ocr, qwen3:14b" className={INPUT} />
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-200">
              <input type="checkbox" checked={v.active} onChange={(e) => setField('active', e.target.checked)} /> aktiv
            </label>
          </div>
        )}
      />

      <FieldsEditor typeId={detail.id} fields={detail.fields} onChanged={onChanged} />
    </div>
  );
}

// ---------------------------------------------------------------
// Felder-Editor — Listen-Pflege in der Zeile (Ausnahme E4): Speichern und
// Abbrechen je Zeile, Löschen mit Rückfrage, Fehler sichtbar an der Zeile.
// ---------------------------------------------------------------
function FieldsEditor({ typeId, fields, onChanged }: {
  typeId: string;
  fields: DocField[];
  onChanged: () => void | Promise<void>;
}) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);

  return (
    <div data-test-anchor="dms-typ-felder">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm uppercase tracking-wider text-slate-400">Metadaten-Felder</h3>
        <button type="button" onClick={() => { setShowNew(true); setEditingId(null); }}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-md bg-slate-800 hover:bg-slate-700 text-slate-100 border border-slate-700 min-h-[44px]">
          <Plus size={12} /> Feld
        </button>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase text-slate-500 border-b border-slate-800">
            <th className="text-left py-1">Slug</th>
            <th className="text-left py-1">Label</th>
            <th className="text-left py-1">Typ</th>
            <th className="text-center py-1">Pflicht</th>
            <th className="text-center py-1">KI</th>
            <th className="w-24"></th>
          </tr>
        </thead>
        <tbody>
          {fields.map((f) => (
            <FieldRow key={f.id} field={f} editing={editingId === f.id}
                      onEdit={() => setEditingId(f.id)}
                      onCancel={() => setEditingId(null)}
                      onSaved={() => { setEditingId(null); void onChanged(); }}
                      onDeleted={() => { setEditingId(null); void onChanged(); }} />
          ))}
          {showNew && (
            <FieldRow field={null} editing typeId={typeId}
                      onCancel={() => setShowNew(false)}
                      onSaved={() => { setShowNew(false); void onChanged(); }} />
          )}
        </tbody>
      </table>
      {fields.length === 0 && !showNew && (
        <div className="text-xs text-slate-500 italic py-2">Noch keine Felder definiert.</div>
      )}
    </div>
  );
}

function FieldRow({ field, editing, typeId, onEdit, onCancel, onSaved, onDeleted }: {
  field: DocField | null;
  editing: boolean;
  typeId?: string;
  onEdit?: () => void;
  onCancel?: () => void;
  onSaved?: () => void;
  onDeleted?: () => void;
}) {
  const dlg = useDialogs();
  const isNew = field === null;
  const [slug, setSlug] = useState(field?.slug ?? '');
  const [label, setLabel] = useState(field?.label ?? '');
  const [kind, setKind] = useState<FieldKind>(field?.kind ?? 'text');
  const [required, setRequired] = useState(field?.required ?? false);
  const [aiExtract, setAiExtract] = useState(field?.ai_extract ?? true);
  const [sortOrder, setSortOrder] = useState<string>(field?.sort_order?.toString() ?? '0');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!field) return;
    setSlug(field.slug); setLabel(field.label); setKind(field.kind);
    setRequired(field.required); setAiExtract(field.ai_extract);
    setSortOrder(field.sort_order.toString());
  }, [field]);

  async function save() {
    setBusy(true); setErr(null);
    try {
      if (isNew) {
        await api(`/doc-types/${typeId}/fields`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ slug, label, kind, required, ai_extract: aiExtract, sort_order: Number(sortOrder) || 0 }),
        });
      } else {
        await api(`/fields/${field.id}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ label, kind, required, ai_extract: aiExtract, sort_order: Number(sortOrder) || 0 }),
        });
      }
      onSaved?.();
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }

  async function del() {
    if (!field) return;
    const ok = await dlg.confirm({
      title: `Feld „${field.label}“ löschen?`,
      message: 'Alle Werte dieses Feldes in Dokumenten gehen verloren.',
      confirmLabel: 'Löschen',
      danger: true,
    });
    if (!ok) return;
    setBusy(true); setErr(null);
    try { await api(`/fields/${field.id}`, { method: 'DELETE' }); onDeleted?.(); }
    catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }

  if (!editing && field) {
    return (
      <tr className="border-b border-slate-800/60 hover:bg-slate-800/40">
        <td className="py-1 font-mono text-xs text-slate-400">{field.slug}</td>
        <td className="py-1 text-slate-100">{field.label}</td>
        <td className="py-1 text-slate-300 text-xs">{FIELD_KINDS.find((k) => k.value === field.kind)?.label ?? field.kind}</td>
        <td className="py-1 text-center">{field.required ? '✓' : ''}</td>
        <td className="py-1 text-center">{field.ai_extract ? '✓' : ''}</td>
        <td className="py-1 text-right">
          <button type="button" onClick={onEdit} className="p-1 text-slate-400 hover:text-slate-100" aria-label="Feld bearbeiten">
            <Pencil size={14} />
          </button>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-b border-slate-800/60 bg-slate-800/40">
      <td className="py-2 pr-2">
        <input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
               disabled={!isNew}
               placeholder="slug"
               className="w-full h-8 px-2 rounded border border-slate-700 bg-slate-900 text-xs font-mono text-slate-100 disabled:opacity-50" />
      </td>
      <td className="py-2 pr-2">
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label"
               className="w-full h-8 px-2 rounded border border-slate-700 bg-slate-900 text-sm text-slate-100" />
      </td>
      <td className="py-2 pr-2">
        <select value={kind} onChange={(e) => setKind(e.target.value as FieldKind)}
                className="w-full h-8 px-2 rounded border border-slate-700 bg-slate-900 text-xs text-slate-100">
          {FIELD_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
        </select>
      </td>
      <td className="py-2 text-center">
        <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} />
      </td>
      <td className="py-2 text-center">
        <input type="checkbox" checked={aiExtract} onChange={(e) => setAiExtract(e.target.checked)} />
      </td>
      <td className="py-2 text-right whitespace-nowrap">
        <button type="button" onClick={() => void save()} disabled={busy || !slug || !label} aria-label="Feld speichern"
                className="p-1 text-emerald-400 hover:text-emerald-200 disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
        </button>
        <button type="button" onClick={onCancel} className="p-1 text-slate-400 hover:text-slate-100" aria-label="Abbrechen">
          <X size={14} />
        </button>
        {!isNew && (
          <button type="button" onClick={() => void del()} disabled={busy} className="p-1 text-rose-400 hover:text-rose-200 disabled:opacity-50" aria-label="Feld löschen">
            <Trash2 size={14} />
          </button>
        )}
        {err && <div className="text-xs text-rose-400 mt-1">{err}</div>}
      </td>
    </tr>
  );
}
